/**
 * BTGalerij — de fotokolom links op de productpagina.
 *
 * Eén reeks foto's die twee vormen aanneemt. Op een telefoon veeg je erdoorheen
 * en volgt de filmstrook mee; op een breed scherm staan ze allemaal onder
 * elkaar en is er niets te volgen. Het script hoeft dat onderscheid niet te
 * kennen: het kijkt of de rij echt scrolt, en doet anders niets.
 */
class BTGalerij {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.section;
    this.index = 0;
    this.scrollWacht = null;
    this.kolom = container.parentElement;
    // De wikkel om allebei de kolommen: daar hangen de cijfers onder als ze
    // smal achter het koopblok moeten staan, want dat staat in de andere sectie.
    this.pagina = container.closest('.bt-product');
    this.proofNa = parseInt(container.dataset.proofNa, 10) || 0;

    this.els = {
      strip: container.querySelector('[data-gal-strip]'),
      slides: Array.from(container.querySelectorAll('[data-gal-slide]')),
      thumbs: Array.from(container.querySelectorAll('[data-gal-thumb]')),
      film: container.querySelector('[data-gal-thumbs]'),
      pijlen: Array.from(container.querySelectorAll('[data-gal-pijl]')),
      keur: container.querySelector('[data-gal-keur]'),
      keurDicht: container.querySelector('[data-gal-keur-dicht]'),
      lb: container.querySelector('[data-gal-lb]'),
      lbFoto: container.querySelector('[data-gal-lb-foto]'),
      dicht: container.querySelector('[data-gal-dicht]'),
      stappen: Array.from(container.querySelectorAll('[data-gal-stap]')),
      // Staan naast deze sectie, niet erin: de balk met de sterren, en het
      // blok met de cijfers en de kaartjes.
      rev: this.kolom && this.kolom.querySelector('.bt-galerij--rev'),
      proof: this.kolom && this.kolom.querySelector('.bt-galerij--na'),
    };

    if (!this.els.slides.length) return;
    this.init();
    this.beginstand();

    // Breed hoort dat blok tussen de foto's, smal eronder. Dat is geen vorm
    // maar een andere plek in de boom, en daar komt CSS niet bij: de foto's
    // staan smal in een veegrij en breed in een raster, en het blok kan niet
    // in allebei op de goede plek staan.
    this.breed = window.matchMedia('(min-width: 960px)');
    this.zetProof(this.breed.matches);
    this.breed.addEventListener('change', (e) => this.zetProof(e.matches));
  }

  init() {
    this.els.slides.forEach((slide, i) => {
      slide.addEventListener('click', () => {
        this.index = i;
        this.vergroot();
      });
    });

    this.els.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.toon(i));
    });

    // De pijlen stappen, ze draaien niet rond: aan het eind van de reeks hoor
    // je niet ineens weer bij foto een terug te komen.
    this.els.pijlen.forEach((pijl) => {
      pijl.addEventListener('click', () => {
        const heen = parseInt(pijl.dataset.galPijl, 10);
        const doel = Math.min(this.els.slides.length - 1, Math.max(0, this.index + heen));
        this.toon(doel);
      });
    });

    // Wegklikken geldt voor deze ene keer. Het onthouden zou betekenen dat
    // iemand die de badge ooit wegklikte hem nooit meer ziet, ook niet als de
    // tekst erin allang iets anders zegt.
    if (this.els.keurDicht && this.els.keur) {
      this.els.keurDicht.addEventListener('click', () => {
        this.els.keur.hidden = true;
        this.container.querySelector('.gal-wrap').classList.remove('gal-wrap--keur');
      });
    }

    if (this.els.film) {
      this.els.film.addEventListener('scroll', () => this.filmRanden(), { passive: true });
      this.filmRanden();
      window.addEventListener('resize', () => this.filmRanden());
    }

    // De filmstrook volgt de vinger. Pas als het scrollen stil ligt bepalen we
    // waar we zijn; tijdens het vegen zou dat tientallen keren per seconde zijn.
    if (this.els.strip) {
      this.els.strip.addEventListener('scroll', () => {
        clearTimeout(this.scrollWacht);
        this.scrollWacht = setTimeout(() => this.uitScroll(), 90);
      }, { passive: true });
    }

    if (this.els.dicht) {
      this.els.dicht.addEventListener('click', () => this.els.lb.close());
    }

    this.els.stappen.forEach((knop) => {
      knop.addEventListener('click', () => this.stap(parseInt(knop.dataset.galStap, 10)));
    });

    if (this.els.lb) {
      // Klikken naast de foto sluit. Het dialoogvenster vult het hele scherm,
      // dus "naast de foto" is een klik die het venster zelf raakt.
      this.els.lb.addEventListener('click', (e) => {
        if (e.target === this.els.lb) this.els.lb.close();
      });

      this.els.lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.stap(-1);
        if (e.key === 'ArrowRight') this.stap(1);
      });
    }
  }

  toon(i, scroll) {
    this.index = (i + this.els.slides.length) % this.els.slides.length;

    this.els.thumbs.forEach((thumb, j) => {
      thumb.classList.toggle('is-aan', j === this.index);
    });

    this.els.pijlen.forEach((pijl) => {
      const heen = parseInt(pijl.dataset.galPijl, 10);
      pijl.disabled = heen < 0 ? this.index === 0 : this.index === this.els.slides.length - 1;
    });

    if (scroll !== false) this.naarSlide();
    this.filmMee();
  }

  zetProof(breed) {
    const { rev, proof } = this.els;
    if (!proof || !this.els.strip || !this.kolom) return;

    const na = breed && this.proofNa > 0 ? this.rijgrens(this.proofNa) : 0;

    // De sterrenbalk hoort altijd vlak onder de foto's: breed schuift hij mee
    // tussen de foto's, smal blijft hij staan waar hij staat, boven de
    // productnaam in het koopblok eronder.
    let anker = na > 0 ? this.els.slides[na - 1] : this.container;
    if (rev) {
      anker.insertAdjacentElement('afterend', rev);
      rev.classList.toggle('is-tussen', na > 0);
      anker = rev;
    }

    // De cijfers en de kaartjes gaan smal naar achteren, onder het koopblok,
    // zodat de naam en de prijs meteen in beeld komen. Breed is daar geen
    // reden voor: daar staat het koopblok ernaast en niet eronder.
    const onder = !breed && this.pagina ? this.pagina : this.kolom;
    if (na > 0) {
      anker.insertAdjacentElement('afterend', proof);
    } else if (proof.parentElement !== onder) {
      onder.appendChild(proof);
    }

    proof.classList.toggle('is-tussen', na > 0);
  }

  /**
   * Na hoeveel foto's het proofblok echt mag staan. Het blok beslaat de volle
   * breedte, dus het hoort aan het eind van een rij. Zet je het er middenin,
   * dan blijft de rest van die rij leeg en staat de foto erna alleen op een
   * eigen rij; daarom schuift het naar de dichtstbijzijnde rijgrens.
   */
  rijgrens(na) {
    // Vraag je om meer foto's dan er zijn, dan bedoel je eronder. Dat naar de
    // laatste rijgrens trekken zou het blok juist omhoog schuiven.
    if (na >= this.els.slides.length) return 0;

    const kolommen = parseInt(this.els.strip.dataset.kolommen, 10) || 2;
    const grenzen = [];
    let kol = 0;

    this.els.slides.forEach((slide, i) => {
      if (slide.classList.contains('is-breed')) {
        // Een brede foto begint zelf een rij, dus de rij ervoor is hoe dan ook af.
        if (kol > 0) grenzen.push(i);
        grenzen.push(i + 1);
        kol = 0;
        return;
      }

      kol += 1;
      if (kol < kolommen) return;
      grenzen.push(i + 1);
      kol = 0;
    });

    // Achter de laatste foto is geen tussenplek meer: dan hoort het blok
    // gewoon onder de foto's, en dat doet zetProof met de nul.
    const keus = grenzen.filter((g) => g < this.els.slides.length);
    if (!keus.length) return 0;

    // Bij een gelijke afstand wint de verste, zodat het blok eerder ná een
    // volle rij staat dan na de ene grote foto bovenaan.
    return keus.reduce((beste, g) => (
      Math.abs(g - na) <= Math.abs(beste - na) ? g : beste
    ), keus[0]);
  }

  /** Vervaag de kant waar nog miniaturen staan, en alleen die kant. */
  filmRanden() {
    const film = this.els.film;
    if (!film) return;

    const speling = film.scrollWidth - film.clientWidth;
    film.classList.toggle('is-meer-links', film.scrollLeft > 4);
    film.classList.toggle('is-meer-rechts', speling > 4 && film.scrollLeft < speling - 4);
  }

  /** Alleen zinvol zolang de rij echt scrolt — breed staat hij stil. */
  scrolt() {
    const strip = this.els.strip;
    return Boolean(strip) && strip.scrollWidth > strip.clientWidth + 1;
  }

  naarSlide() {
    if (!this.scrolt()) return;
    this.els.strip.scrollTo({ left: this.els.slides[this.index].offsetLeft, behavior: 'smooth' });
  }

  /** Houd de actieve miniatuur in beeld, ook als je ver doorveegt. */
  filmMee() {
    const thumb = this.els.thumbs[this.index];
    if (!thumb || !thumb.parentElement) return;

    const strook = thumb.parentElement;
    if (strook.scrollWidth <= strook.clientWidth + 1) return;

    const links = thumb.offsetLeft - (strook.clientWidth - thumb.offsetWidth) / 2;
    strook.scrollTo({ left: Math.max(0, links), behavior: 'smooth' });
  }

  /** Eerste keer: zet de pijlen en de randen goed zonder iets te verschuiven. */
  beginstand() {
    this.toon(0, false);
  }

  uitScroll() {
    if (!this.scrolt()) return;

    const strip = this.els.strip;
    const midden = strip.scrollLeft + strip.clientWidth / 2;
    let dichtst = 0;
    let besteAfstand = Infinity;

    this.els.slides.forEach((slide, i) => {
      const afstand = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - midden);
      if (afstand < besteAfstand) {
        besteAfstand = afstand;
        dichtst = i;
      }
    });

    if (dichtst !== this.index) this.toon(dichtst, false);
  }

  vergroot() {
    if (!this.els.lb || !this.els.lbFoto) return;

    this.zetGrote();
    this.els.lb.showModal();
  }

  zetGrote() {
    const slide = this.els.slides[this.index];
    const foto = slide.querySelector('img');

    this.els.lbFoto.src = slide.dataset.groot || (foto && foto.currentSrc) || '';
    this.els.lbFoto.alt = foto ? foto.alt : '';
  }

  stap(richting) {
    this.toon(this.index + richting);
    this.zetGrote();
  }
}

function btGalerijStart(wortel) {
  (wortel || document).querySelectorAll('[data-bt-galerij]').forEach((el) => {
    if (el.dataset.galKlaar) return;
    el.dataset.galKlaar = '1';
    new BTGalerij(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => btGalerijStart());
} else {
  btGalerijStart();
}

// De theme editor bouwt een sectie opnieuw op na een wijziging.
document.addEventListener('shopify:section:load', (e) => btGalerijStart(e.target));